import { useCallback, useEffect, useRef } from 'react';

import { parseGeneratedAgenda, type GeneratedAgenda } from './agendaViewModel';

import type { AiResultNotification } from '@/api/ai-gateway/aiHub';

import { useAmelioTranslations } from '@/hooks';
import { useAiResultContext } from '@/hooks/ai/AiResultProvider';
import { AI_GENERATION_WAIT_LIMIT_MS } from '@/hooks/ai/useAiGeneration';
import { showErrorToast } from '@/plugins/Toast';

/** What the manager typed in the agenda modal, sent with the correlation id the result will come under. */
export interface AgendaGenerationInput {
    correlationId: string;
    objectives: string;
    context: string;
}

/**
 * Posts an agenda generation request. When the backend attaches the caller to a generation already in flight,
 * it answers with that generation's correlationId, which is the one to wait on.
 */
export type AgendaGenerationRequest = (
    input: AgendaGenerationInput,
) => Promise<{ correlationId?: string } | undefined>;

/**
 * Drives AI generation of a meeting agenda through `request` and returns the parsed result (both languages).
 * The backend does not persist the agenda — it streams the JSON back over SignalR and the caller decides how
 * to integrate it (Add / Replace) and saves it through its own update endpoint.
 *
 * `generate` resolves to null on failure or timeout; `generateOrReport` does the same after telling the
 * manager, for a generation they just asked for.
 *
 * A promise rather than `useAiGeneration`'s idle/pending/failed state: the AI prompt modal awaits the
 * generation it starts, and shows its own progress meanwhile.
 */
export function useAgendaGeneration(request: AgendaGenerationRequest) {
    const { t } = useAmelioTranslations();
    const { subscribe } = useAiResultContext();

    const generate = useCallback(
        async (objectives: string, context: string): Promise<GeneratedAgenda | null> => {
            const ownId = crypto.randomUUID();
            // The correlationId we ultimately wait on: the one we send, unless the backend attaches us to an
            // already in-flight request (status "pending") and returns its correlationId instead. Held in a
            // mutable variable the subscription reads at match time, so it can be updated after the POST.
            let effectiveId: string = ownId;
            let settle: (notification: AiResultNotification | null) => void = () => {};

            // Arm the subscription before posting, so a fast result can't slip through before we listen.
            const resultPromise = new Promise<AiResultNotification | null>((resolve) => {
                const timeout = window.setTimeout(() => settle(null), AI_GENERATION_WAIT_LIMIT_MS);
                const unsubscribe = subscribe((notification) => {
                    if (notification.correlationId === effectiveId) settle(notification);
                });
                settle = (notification) => {
                    window.clearTimeout(timeout);
                    unsubscribe();
                    resolve(notification);
                };
            });

            try {
                const response = await request({ correlationId: ownId, objectives, context });
                if (response?.correlationId) {
                    effectiveId = response.correlationId;
                }
            } catch {
                settle(null);
                return null;
            }

            const notification = await resultPromise;
            if (!notification?.success || !notification.content) return null;

            return parseGeneratedAgenda(notification.content);
        },
        [request, subscribe],
    );

    // Read through a reference: the translation function is new on every render, and callers run
    // `generateOrReport` from effects that must not start again for that.
    const translate = useRef(t);
    useEffect(() => {
        translate.current = t;
    });

    const generateOrReport = useCallback(
        async (objectives: string, context: string) => {
            const generated = await generate(objectives, context);
            if (!generated) {
                showErrorToast({
                    message: translate.current('meetingAgenda.generateError'),
                });
            }
            return generated;
        },
        [generate],
    );

    return { generate, generateOrReport };
}
